import { lazy } from 'react';

/**
 * Chunk-load-resilient wrapper around React.lazy.
 *
 * Solves two failure modes we saw after enabling code-splitting on production:
 *
 * 1. Stale HTML after a deploy. When we ship a new bundle, all chunk
 *    filenames change (Webpack contenthash). Users with the previous
 *    HTML cached will try to `import()` a hash that no longer exists on
 *    the server and get "Loading chunk XXX failed". Solution: catch the
 *    error and force a hard reload so the browser fetches the current
 *    HTML + fresh chunk names.
 *
 * 2. Transient network flakes / CDN timeouts. Occasionally a chunk
 *    request just fails. Solution: retry the import once after a short
 *    delay before falling through to the reload path.
 *
 * We only force-reload once per session (tracked via sessionStorage) so
 * we never loop reloads if a chunk is genuinely missing.
 */

const RELOAD_FLAG = 'epf.chunk_reload_attempted';

const isChunkLoadError = (err) => {
  const name = err?.name || '';
  const message = err?.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [^\s]+ failed/i.test(message) ||
    /Loading CSS chunk [^\s]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
};

const importWithRetry = (importer, attemptsLeft = 1) =>
  importer().catch((err) => {
    if (attemptsLeft > 0 && isChunkLoadError(err)) {
      // Wait a beat, then retry once. Fixes flaky-network failures without
      // reloading the whole page unnecessarily.
      return new Promise((resolve) =>
        setTimeout(() => resolve(importWithRetry(importer, attemptsLeft - 1)), 600),
      );
    }
    if (isChunkLoadError(err)) {
      // Stale-HTML case: our current page references chunk names the server
      // no longer has. A hard reload gets the fresh HTML + new chunk names.
      // Guard against reload loops in case the deploy is genuinely broken.
      try {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
          window.location.reload();
          // Return a never-resolving promise so React doesn't try to render
          // an error state before the reload fires.
          return new Promise(() => {});
        }
      } catch {
        // sessionStorage unavailable (private mode / SSR) — fall through.
      }
    }
    throw err;
  });

const lazyWithRetry = (importer) => lazy(() => importWithRetry(importer));

export default lazyWithRetry;
